import { gql, useMutation, useQuery } from "@apollo/client";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { AppText, FlexDiv, SkeletonBlock } from "src/Components/UI";
import { PhoenixAppButton, PhoenixIcon } from "src/Components/UI/Phoenix";
import {
  AddAssociatedContact,
  AddOrEditContactModal,
  EditCallResultModal,
  EditCustomFieldsComponent,
  MergeContactsModal,
  Modal,
  ReassignLeadModalComponent,
  UpdateLeadDataComponent,
  UpdateProductModal,
} from "src/Components/modal";
import { SelectProductModal } from "src/Components/modal/SelectProductModal";
import { loggedInUser } from "src/apollo/cache";
import { CallContext, ModalContext } from "src/context";
import { edit, google } from "src/images/NewDesign";
import { MixpanelActions } from "src/services/mixpanel";
import { formatBusinessName } from "src/utils/format";
import { LEAD_OWNERSHIP_STATUS } from "src/utils/misc";
import { theme } from "src/utils/theme";
import { appToast, errorToast } from "src/utils/toast";
import { APP_REP_ROLES, APP_ROLES } from "src/utils/variables";
import styled, { keyframes } from "styled-components";
import { DeleteProductModalV2, FullEmailModal, PanelModal, UpdateTermsModal } from "../../modal";
import { SearchMenuV2, SequenceTag } from "../DashboardV2";
import { LeadDetailV3 } from "../LeadDetailV3";
import { AggregatedLeadHistory, LeadDataColumn, NoteSection } from "./";

const FETCH_LEAD = gql`
  query fetchLeadDetail($id: String!) {
    fetchLead(id: $id) {
      id
      favorited
      primary_phone_number
      conference_number
      current_lead_type
      city
      country
      lead_value {
        value
      }
      timezone_by_state
      lead_source
      industry
      sub_industry
      address
      channel
      business_name
      full_name
      first_name
      last_name
      state
      rep {
        id
        first_name
        last_name
        profile_image
      }
      lead_ownership_status
      computed_mrr
      current_close_date

      primary_email
      primary_email_title
      primary_email_type

      primary_phone_number_country_code
      local_primary_phone_number
      primary_phone_number_title
      primary_phone_number_type

      alternate_contacts {
        id
        title
        channel
        type
        value
        mapping_order
        contact_label
        country_code
      }

      flagged_numbers: LeadFlaggedNumber {
        id
        phone_number
        lead_activity {
          related_disposition {
            id
            type
          }
        }
      }

      next_intent_scheduled_or_unscheduled {
        id
        type
        replacement_lead_intent_id
        anytime_day
        event_type_label
        general_time_start_date
        general_time_end_date
        dial_num_in_sales_cycle
        current_sequence_step
      }

      next_scheduled_event {
        id
        type
      }
      next_scheduled_event_time

      pipeline_state_label
      is_in_active_queue

      next_lead_intent {
        id
        schedule_item {
          id
          deleted_at
        }
      }

      sequence_id
      sequence_step_id
      sequence_step {
        actions {
          id
          task
          custom_task_note
          scheduled_event_behavior
        }
      }

      visible_custom_fields {
        id
        key
        value
        visible
        boo_value
        num_value
        date_value
        type
        lead_id
        options
        computed_value
        list_value
        allow_reps_to_edit
      }

      last_lead_activity {
        id
        type
        created_at
      }

      brand {
        id
      }

      organization {
        id
        name
        allow_remove_step_action
        allow_snooze_step_action
        double_booking
      }

      email_sub_status
      sms_sub_status
    }
  }
`;

const FETCH_LEAD_SALE_MADE = gql`
  query fetchAllSales($lead_id: String!, $associate_lead_ids: [String!]!) {
    fetchAllSales(lead_id: $lead_id, associate_lead_ids: $associate_lead_ids) {
      id
      products
    }
  }
`;

const FETCH_CUSTOM_OBJECT_SUMMARY = gql`
  query fetchAssociatedCustomObjects($lead_id: String!) {
    fetchAssociatedCustomObjects(lead_id: $lead_id) {
      id
      name
      association_count
    }
  }
`;

const FETCH_ASSOCIATED_LEADS = gql`
  query fetchAssociateContact($primaryLeadId: String!) {
    fetchAssociateContact(primary_lead_id: $primaryLeadId) {
      id
      first_name
      last_name
      full_name
      conference_number
      primary_phone_number
      local_primary_phone_number
      primary_phone_number_country_code
      primary_phone_number_title
      primary_phone_number_type
      primary_email
      primary_email_title
      primary_email_type
      lead_ownership_status
      rep_id
      country
      alternate_contacts {
        id
        title
        channel
        type
        value
      }
      next_intent_scheduled_or_unscheduled {
        id
        event_type_label
        dial_num_in_sales_cycle
        anytime_before #general time
        anytime_after
        anytime_day
        anytime_day_upperbound
        anytime_tz
        general_time_start_date
        general_time_end_date
        current_sequence_step
        schedule_item {
          #specific time
          id
          start_time
          end_time
        }
        replacement_lead_intent_id
      }
    }
  }
`;

const FETCH_NOTE_HISTORY = gql`
  query fetchNoteHistory($lead_id: String!, $event_filter: HistoryEventTypeOption, $show_all_lead: Boolean) {
    fetchLeadHistory(lead_id: $lead_id, event_filter: $event_filter, show_all_lead: $show_all_lead) {
      id
      created_at
      notes
      html
      user {
        id
        full_name
        phone_number
        profile_image
      }
      is_pinned_to_top
    }
  }
`;

const CLAIM_LEAD = gql`
  mutation claimLead($lead_id: String!) {
    claimLead(lead_id: $lead_id) {
      id
      lead {
        id
      }
    }
  }
`;

export const LeadDetailV4: React.FC = () => {
  const {
    reassignLeadModal,
    setReassignLeadModal,
    selectedLeadToReassign,
    setSelectedLeadToReassign,
    setEditModalLeadId,
    setEditModal,
    editModal,
    editModalLeadId,
    showAddOrEditContactModal,
    setShowAddOrEditContactModal,
    showAddAssociateModal,
    setShowAddAssociateModal,
    showMergeContactsModal,
    showCustomFieldsModal,
    closeCustomFieldsModal,
    customFieldsData,
    showUpdateProductModal,
    setShowUpdateProductModal,
    showDeleteProductModal,
    showSelectProductModal,
    showUpdateTermsModal,
    setShowUpdateTermsModal,
    showEditCallResultModal,
  } = useContext(ModalContext);
  const { useLegacyDialer, logNonCallActivity, callState } = useContext(CallContext);

  const { lead_id } = useParams<{ lead_id: string }>();

  const history = useHistory();

  const [showSearchMenu, setShowSearchMenu] = useState(false);

  const { data: dataLead, loading: loadingLead, error: errorLead, refetch: refetchLead } = useQuery(FETCH_LEAD, {
    fetchPolicy: "no-cache",
    skip: !lead_id,
    variables: { id: lead_id },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
      errorToast("Error fetching lead.");
    },
  });
  const leadData = useMemo(() => dataLead?.fetchLead, [dataLead]);

  const canDialCustomers = loggedInUser()?.organization?.enable_rep_to_connect_customers;

  const isOwnedByRep =
    ["Owned", "Assigned"].includes(leadData?.lead_ownership_status) && leadData?.rep?.id === loggedInUser()?.id;
  const inCallableStatus = [
    LEAD_OWNERSHIP_STATUS.Owned,
    LEAD_OWNERSHIP_STATUS.Assigned,
    ...(canDialCustomers ? [LEAD_OWNERSHIP_STATUS.Customer] : []),
  ].includes(leadData?.lead_ownership_status);

  const canReassign = useMemo(() => {
    if (loadingLead) return false;
    if (["ADMIN", "SM"].includes(loggedInUser().role ?? "")) return true;
    return dataLead?.fetchLead?.rep?.id === loggedInUser().id && dataLead?.fetchLead?.organization?.double_booking;
  }, [dataLead, loadingLead]);

  const { data: dataAssociated } = useQuery(FETCH_ASSOCIATED_LEADS, {
    variables: {
      primaryLeadId: lead_id,
    },
    skip: !lead_id,
    fetchPolicy: "no-cache",
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const { data: listDataSale } = useQuery(FETCH_LEAD_SALE_MADE, {
    skip: !lead_id || !dataAssociated,
    variables: {
      lead_id: lead_id,
      associate_lead_ids: dataAssociated?.fetchAssociateContact?.length
        ? dataAssociated?.fetchAssociateContact?.map((item: any) => item?.id)
        : [],
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: dataCustomObjectSummary, loading: loadingCustomObjectSummary } = useQuery(FETCH_CUSTOM_OBJECT_SUMMARY, {
    fetchPolicy: "network-only",
    variables: {
      lead_id: lead_id,
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const { data: noteHistoryData } = useQuery(FETCH_NOTE_HISTORY, {
    fetchPolicy: "cache-and-network",
    variables: {
      lead_id: lead_id,
      event_filter: "Note",
      show_all_lead: false,
    },
  });

  const [claimLead, { loading: claimLoading }] = useMutation(CLAIM_LEAD, {
    onCompleted({ claimLead }) {
      appToast("Lead claimed successfully!");
      refetchLead();
    },
    onError({ message, name }) {
      errorToast("Error claiming lead.");
      console.log(`Error in ${name}: `, message);
    },
    refetchQueries: ["fetchLead", "fetchAssociateContact", "fetchAggregatedLeadHistory"],
  });

  const handleClaimLead = useCallback(() => {
    if (!lead_id) {
      errorToast("No lead ID found.");
      return;
    }

    claimLead({
      variables: {
        lead_id: lead_id,
      },
    });
  }, [lead_id, claimLead]);

  const showClaimLeadButton = useMemo(() => {
    const ableToClaimLead =
      leadData?.lead_ownership_status === "Unassigned" || leadData?.lead_ownership_status === "Assigned";

    const userIsRep = APP_REP_ROLES.includes(loggedInUser()?.role as APP_ROLES);

    return !loadingLead && ableToClaimLead && userIsRep && !isOwnedByRep;
  }, [leadData, loggedInUser, loadingLead, isOwnedByRep]);

  const leadIsClosed = !!listDataSale?.fetchAllSales?.length;
  const titleIsBusinessName = loggedInUser()?.organization?.title_is_business_name;

  const inSequence = useMemo(() => leadData?.sequence_step_id, [leadData]);

  const formatLeadCardTitle = useCallback(() => {
    if (titleIsBusinessName) {
      return formatBusinessName(leadData?.business_name);
    }
    return leadData?.full_name || leadData?.address || "NA";
  }, [titleIsBusinessName, leadData]);

  const contactOptions = useMemo(
    () => [
      { label: "All", value: undefined },
      {
        label:
          leadData?.first_name && leadData?.last_name ? `${leadData?.first_name} ${leadData?.last_name}` : "No Name",
        value: leadData?.id,
      },
      ...(!!dataAssociated?.fetchAssociateContact?.length
        ? dataAssociated?.fetchAssociateContact?.map((item: any) => {
            return {
              label: item?.first_name && item?.last_name ? `${item?.first_name} ${item?.last_name}` : "No Name",
              value: item?.id,
            };
          })
        : []),
    ],
    [leadData, dataAssociated],
  );

  if (useLegacyDialer) return <LeadDetailV3 />;
  return (
    <Main>
      {reassignLeadModal && !!selectedLeadToReassign.lead_id && <ReassignLeadModalComponent trackMixpanel />}
      {editModal && (
        <Modal open={editModal} onClose={() => setEditModal(false)} closeButtonSize={16}>
          <UpdateLeadDataComponent
            customHeight
            visible={editModal}
            close={() => {
              setEditModal(false);
              setEditModalLeadId("");
            }}
            lead_id={editModalLeadId}
          />
        </Modal>
      )}
      {showEditCallResultModal && <EditCallResultModal />}
      {showAddOrEditContactModal && (
        <div style={{ position: "fixed", zIndex: 9999, width: "472px", maxHeight: "98vh", overflowY: "auto" }}>
          <AddOrEditContactModal setShowAddContact={setShowAddOrEditContactModal} />
        </div>
      )}
      {showAddAssociateModal && (
        <div style={{ position: "fixed", zIndex: 9999, width: "472px", maxHeight: "98vh", overflowY: "auto" }}>
          <AddAssociatedContact setShowAddAssociatedContact={setShowAddAssociateModal} />
        </div>
      )}
      {showMergeContactsModal && (
        <div
          style={{
            position: "fixed",
            zIndex: 9999,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: "800px", height: "600px" }}>
            <MergeContactsModal />
          </div>
        </div>
      )}
      {showCustomFieldsModal && (
        <DarkDiv2>
          <SlideInDiv>
            <EditCustomFieldsComponent
              showEditCustomFields={showCustomFieldsModal}
              closeCustomFieldsModal={closeCustomFieldsModal}
              fields={customFieldsData?.lead.visible_custom_fields}
              lead_id={customFieldsData?.lead?.id}
            />
          </SlideInDiv>
        </DarkDiv2>
      )}

      {showUpdateProductModal && (
        <PanelModal open={showUpdateProductModal} onClose={() => setShowUpdateProductModal(false)}>
          <UpdateProductModal />
        </PanelModal>
      )}
      {showDeleteProductModal && (
        <DarkDiv2>
          <DeleteProductModalV2 />
        </DarkDiv2>
      )}
      {showSelectProductModal && (
        <DarkDiv2>
          <SelectProductModal />
        </DarkDiv2>
      )}
      {showUpdateTermsModal && (
        <PanelModal open={showUpdateTermsModal} onClose={() => setShowUpdateTermsModal(false)}>
          <UpdateTermsModal />
        </PanelModal>
      )}
      <FullEmailModal />

      <Header>
        {showSearchMenu && (
          <SearchMenuV2
            setIsSearchClicked={setShowSearchMenu}
            businessName={leadData?.business_name}
            fullName={leadData?.full_name}
            city={leadData?.city}
            top="60px"
            left="186px"
            right={null}
          />
        )}

        <FlexDiv gap={24}>
          <FlexDiv direction="column" gap={8}>
            {loadingLead ? (
              <SkeletonBlock width={152} height={28} borderRadius={4} />
            ) : (
              <FlexDiv align="center" gap={8}>
                <PhoenixIcon
                  svg={edit}
                  size={16}
                  pointer
                  onClick={() => {
                    setEditModalLeadId(lead_id);
                    setEditModal(true);
                  }}
                />
                <FadeInText fontSize={22} fontWeight={500} lineHeight={28}>
                  {formatLeadCardTitle()}
                </FadeInText>
              </FlexDiv>
            )}

            {!loadingLead && leadIsClosed && (
              <KeyEventDiv
                style={{
                  backgroundColor: theme.SUCCESS500,
                }}
                key={`sale-sale`}
              >
                <AppText fontSize={8} fontWeight={600} variant="white">
                  SALE CLOSED
                </AppText>
              </KeyEventDiv>
            )}
          </FlexDiv>

          {!loadingLead && (
            <FlexDiv gap={8} align="center" padding="2px 0px 0px 0px" style={{ marginBottom: "auto" }}>
              <img
                src={google}
                style={{ width: "22px", height: "22px", cursor: "pointer" }}
                loading="lazy"
                alt={`google ${leadData?.business_name || "NA"}`}
                onClick={() => setShowSearchMenu(true)}
              />
              {inSequence && <SequenceTag />}
            </FlexDiv>
          )}
        </FlexDiv>

        <FlexDiv direction="column" gap={8}>
          <FlexDiv align="center" gap={4} style={{ marginLeft: "auto" }}>
            {loadingLead ? (
              <SkeletonBlock width={160} height={20} borderRadius={4} />
            ) : leadData?.rep?.id ? (
              <>
                <FadeInText fontSize={12} fontWeight={500} lineHeight={18}>
                  Assigned to&nbsp;
                </FadeInText>
                <div style={{ padding: "4px 8px", backgroundColor: theme.fill.brand.secondary, borderRadius: "4px" }}>
                  <FadeInText fontSize={8} fontWeight={600} lineHeight={12} uppercase color={theme.text.brand.primary}>
                    {dataLead?.fetchLead?.rep?.first_name ?? ""} {dataLead?.fetchLead?.rep?.last_name ?? ""}
                  </FadeInText>
                </div>
              </>
            ) : (
              <FadeInText fontSize={12} fontWeight={500} lineHeight={18}>
                Lead not assigned
              </FadeInText>
            )}
          </FlexDiv>
          <FlexDiv align="center" gap={8}>
            {canReassign && (
              <PhoenixAppButton
                buttonType="ghost-small"
                variant="brand-outline"
                style={{ marginLeft: "auto" }}
                onClick={() => {
                  MixpanelActions.track("Lead Detail", {
                    type: "Reassign Clicked",
                  });
                  setSelectedLeadToReassign({
                    lead_id: lead_id,
                    source_user_id: leadData?.rep?.id ?? "",
                    lead_id_list: [],
                  });
                  setReassignLeadModal(!reassignLeadModal);
                }}
              >
                <AppText
                  fontSize={10}
                  fontWeight={600}
                  lineHeight={16}
                  letterSpacing={1}
                  uppercase
                  color={theme.text.brand.primary}
                  style={{ padding: "0px 4px" }}
                >
                  Reassign Lead
                </AppText>
              </PhoenixAppButton>
            )}
            {showClaimLeadButton && (
              <PhoenixAppButton
                buttonType="ghost-small"
                variant="brand-outline"
                style={{ marginLeft: "auto" }}
                onClick={handleClaimLead}
                disabled={claimLoading}
              >
                <AppText
                  fontSize={10}
                  fontWeight={600}
                  lineHeight={16}
                  letterSpacing={1}
                  uppercase
                  color={theme.text.brand.primary}
                  style={{ padding: "0px 4px" }}
                >
                  Claim Lead
                </AppText>
              </PhoenixAppButton>
            )}
            <PhoenixAppButton
              buttonType="ghost-small"
              variant="brand-outline"
              style={{ marginLeft: "auto" }}
              onClick={() => {
                history.push(`/dash`);
                logNonCallActivity(lead_id || "");
              }}
              disabled={!isOwnedByRep || !inCallableStatus || !!callState}
            >
              <AppText
                fontSize={10}
                fontWeight={600}
                lineHeight={16}
                letterSpacing={1}
                uppercase
                color={theme.text.brand.primary}
                style={{ padding: "0px 4px" }}
              >
                Add Activity
              </AppText>
            </PhoenixAppButton>
          </FlexDiv>
        </FlexDiv>
      </Header>

      <Body>
        <LeadDataColumn
          leadData={leadData}
          associatedContacts={dataAssociated?.fetchAssociateContact}
          dataCustomObjectSummary={dataCustomObjectSummary}
        />

        <AggregatedLeadHistory leadId={lead_id} />

        <NoteSection
          leadData={leadData}
          contactOptions={contactOptions}
          noteHistoryData={noteHistoryData?.fetchLeadHistory}
        />
      </Body>
    </Main>
  );
};

const Main = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;

  background-color: ${theme.surface.brand.primary};
`;

const Header = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 108px;

  padding: 24px 40px;

  background-color: ${theme.fill.neutral.primary};
  border-bottom: 1px solid ${theme.border.neutral.primary};
`;

const Body = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 46px;

  height: 100%;
`;

const FadeInText = styled(AppText)`
  animation: ${theme.fadeIn} 0.2s ease-in-out forwards;
`;

const KeyEventDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;

  width: fit-content;
  height: fit-content;
  padding: 4px 8px 4px 8px;

  border-radius: 4px;
  color: ${theme.WHITE_COLOR};
`;

// TODO: cleanup the modal wrapper styled components. Currently taken from dash V1
const slideInAnimation = keyframes`
  0% { margin-left: 0px; width: 0px; }
  100% { margin-left: 0px; width: 552px; }
`;
const DarkDiv2 = styled.div`
  position: fixed;
  top: 0px;
  bottom: 0px;
  left: -72px;
  right: 0px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;
const SlideInDiv = styled.div`
  position: absolute;
  left: 72px;
  width: calc(478px + 72px);
  min-height: 100vh;
  overflow: hidden;
  animation-name: ${slideInAnimation};
  animation-duration: 700ms;
  z-index: 8;
  background-color: ${theme.WHITE_COLOR};
`;
